import type { AlertBlockStateType, AlertComponentType } from '../../blocks/Alert/types';
import type {
  AudioPlayerBlockStateType,
  AudioPlayerComponentType,
} from '../../blocks/AudioPlayer/types';
import type {
  AudioRecorderBlockStateType,
  AudioRecorderComponentType,
} from '../../blocks/AudioRecorder/types';
import type { AvatarBlockStateType, AvatarComponentType } from '../../blocks/Avatar/types';
import type {
  AvatarGroupBlockStateType,
  AvatarGroupComponentType,
} from '../../blocks/AvatarGroup/types';
import type {
  AxisCommentsBlockStateType,
  AxisCommentsComponentType,
  ComputedAxisCommentsBlockStateType,
} from '../../blocks/AxisComments/types';
import type {
  AxisIconPickerBlockStateType,
  AxisIconPickerComponentType,
  ComputedAxisIconPickerBlockStateType,
} from '../../blocks/AxisIconPicker/types';
import type {
  AxisListBlockStateType,
  AxisListComponentType,
  ComputedAxisListBlockStateType,
} from '../../blocks/AxisList/types';
import type {
  AxisNewIssueModalBlockStateType,
  AxisNewIssueModalComponentType,
  ComputedAxisNewIssueModalBlockStateType,
} from '../../blocks/AxisNewIssueModal/types';
import type {
  AxisNewProjectModalBlockStateType,
  AxisNewProjectModalComponentType,
  ComputedAxisNewProjectModalBlockStateType,
} from '../../blocks/AxisNewProjectModal/types';
import type {
  AxisMultiSelectPickerBlockStateType,
  AxisMultiSelectPickerComponentType,
  AxisSingleSelectPickerBlockStateType,
  AxisSingleSelectPickerComponentType,
  ComputedAxisMultiSelectPickerBlockStateType,
  ComputedAxisSingleSelectPickerBlockStateType,
} from '../../blocks/AxisPicker/blockTypes';
import type {
  AxisRichTextEditorBlockStateType,
  AxisRichTextEditorComponentType,
  ComputedAxisRichTextEditorBlockStateType,
} from '../../blocks/AxisRichTextEditor/types';
import type {
  AxisSubIssueComposerBlockStateType,
  AxisSubIssueComposerComponentType,
  ComputedAxisSubIssueComposerBlockStateType,
} from '../../blocks/AxisSubIssueComposer/types';
import type { BarcodeBlockStateType, BarcodeComponentType } from '../../blocks/Barcode/types';
import type {
  BottomSheetBlockStateType,
  BottomSheetComponentType,
} from '../../blocks/BottomSheet/types';
import type {
  BusinessHoursBlockStateType,
  BusinessHoursComponentType,
  BusinessHourValueType,
} from '../../blocks/BusinessHours/types';
import type {
  ButtonGroupBlockStateType,
  ButtonGroupComponentType,
} from '../../blocks/ButtonGroup/types';
import type { CalendarBlockStateType, CalendarComponentType } from '../../blocks/Calendar/types';
import type {
  CarouselComponentStateType,
  CarouselComponentType,
} from '../../blocks/Carousel/types';
import type { CascaderBlockStateType, CascaderComponentType } from '../../blocks/Cascader/types';
import type {
  CategoryFilterComponentType,
  CategoryFilterStateType,
} from '../../blocks/CategoryFilter/types';
import type {
  ChartComponentType,
  ChartStateType,
  ComputedChartStateType,
} from '../../blocks/Chart/types';
import type { ChatBlockStateType, ChatComponentType } from '../../blocks/Chat/types';
import type { CitationBlockStateType, CitationComponentType } from '../../blocks/Citation/types';
import type {
  CodeEditorBlockStateType,
  CodeEditorComponentType,
} from '../../blocks/CodeEditor/types';
import type {
  CommentInputBlockStateType,
  CommentInputComponentType,
} from '../../blocks/CommentInput/types';
import type {
  CommentListBlockStateType,
  CommentListComponentType,
  CommentListComputedBlockStateType,
} from '../../blocks/CommentList/types';
import type { CommentsBlockStateType, CommentsComponentType } from '../../blocks/Comments/types';
import type { ContainerBlockStateType, ContainerComponentType } from '../../blocks/Container/types';
import type {
  ContextualDialogBlockStateType,
  ContextualDialogComponentType,
} from '../../blocks/ContextualDialog/types';
import type { CopilotBlockStateType, CopilotComponentType } from '../../blocks/Copilot/types';
import type { ChatComponentType as CopilotChatComponentType } from '../../blocks/CopilotChat/types';
import type {
  CopilotHistoryBlockStateType,
  CopilotHistoryComponentType,
} from '../../blocks/CopilotHistory/types';
import type {
  CopilotIframeBlockStateType,
  CopilotIframeComponentType,
} from '../../blocks/CopilotIframe/types';
import type {
  CopilotInputBlockStateType,
  CopilotInputComponentType,
  CopilotInputComputedBlockStateType,
} from '../../blocks/CopilotInput/types';
import type {
  CopilotResponseBlockStateType,
  CopilotResponseComponentType,
} from '../../blocks/CopilotResponse/types';
import type {
  CopilotThreadBlockStateType,
  CopilotThreadComponentType,
} from '../../blocks/CopilotThread/types';
import type {
  CopilotTypographyBlockStateType,
  CopilotTypographyComponentType,
} from '../../blocks/CopilotTypography/types';
import type {
  CopilotVideoBlockStateType,
  CopilotVideoComponentType,
} from '../../blocks/CopilotVideo/types';
import type {
  CopilotVoiceBlockStateType,
  CopilotVoiceComponentType,
} from '../../blocks/CopilotVoice/types';
import type {
  CopilotVoiceTranscriptsBlockStateType,
  CopilotVoiceTranscriptsComponentType,
} from '../../blocks/CopilotVoiceTranscripts/types';
import type {
  ComputedCustomBlockStateType,
  CustomBlockStateType,
  CustomComponentType,
} from '../../blocks/Custom/types';
import type {
  DatePickerBlockStateType,
  DatePickerComponentType,
} from '../../blocks/DatePicker/types';
import type { DictationBlockStateType, DictationComponentType } from '../../blocks/Dictation/types';
import type { DrawerBlockStateType, DrawerComponentType } from '../../blocks/Drawer/types';
import type {
  EventsTimelineBlockStateType,
  EventsTimelineComponentType,
} from '../../blocks/EventsTimeline/types';
import type {
  FileUploadBlockStateType,
  FileUploadComponentType,
} from '../../blocks/FileUpload/types';
import type {
  FlippableCardBlockStateType,
  FlippableCardComponentType,
} from '../../blocks/FlippableCard/types';
import type {
  FloatingActionButtonBlockStateType,
  FloatingActionButtonComponentType,
} from '../../blocks/FloatingActionButton/types';
import type { FormBlockStateType, FormComponentType } from '../../blocks/Form/types';
import type {
  HeadlessCopilotBlockStateType,
  HeadlessCopilotComponentType,
} from '../../blocks/HeadlessCopilot/types';
import type { IconBlockStateType, IconComponentType } from '../../blocks/Icon/types';
import type {
  IconButtonBlockStateType,
  IconButtonComponentType,
} from '../../blocks/IconButton/types';
import type { IframeBlockStateType, IframeComponentType } from '../../blocks/Iframe/types';
import type { ImageBlockStateType, ImageComponentType } from '../../blocks/Image/types';
import type { ImageGridBlockStateType, ImageGridComponentType } from '../../blocks/ImageGrid/types';
import type {
  ComputedKnowledgeBuilderBlockStateType,
  KnowledgeBuilderBlockStateType,
  KnowledgeBuilderComponentType,
} from '../../blocks/KnowledgeBuilder/types';
import type {
  LinkPreviewBlockStateType,
  LinkPreviewComponentType,
} from '../../blocks/LinkPreview/types';
import type {
  LocationPreviewBlockStateType,
  LocationPreviewComponentType,
} from '../../blocks/LocationPreview/types';
import type {
  LottieAnimationBlockStateType,
  LottieAnimationComponentType,
} from '../../blocks/LottieAnimation/types';
import type { MediaBlockStateType, MediaComponentType } from '../../blocks/Media/types';
import type { MeetingBlockStateType, MeetingComponentType } from '../../blocks/Meeting/types';
import type { MenuComponentStateType, MenuComponentType } from '../../blocks/Menu/types';
import type {
  MessageInputBlockStateType,
  MessageInputComponentType,
  MessageInputComputedBlockStateType,
} from '../../blocks/MessageInput/types';
import type {
  ComputedModuleBlockStateType,
  ModuleBlockStateType,
  ModuleComponentType,
} from '../../blocks/Module/types';
import type {
  MultiStatsCardBlockStateType,
  MultiStatsCardComponentType,
} from '../../blocks/MultiStatsCard/types';
import type {
  NavigationContainerComponentType,
  NavigationContainerStateType,
} from '../../blocks/NavigationContainer/types';
import type {
  NavigationMenuComponentType,
  NavigationMenuStateType,
} from '../../blocks/NavigationMenu/types';
import type {
  OrganizationChartBlockStateType,
  OrganizationChartComponentType,
  OrganizationChartComputedBlockStateType,
} from '../../blocks/OrganizationChart/types';
import type { PasswordComponentType, PasswordStateType } from '../../blocks/Password/types';
import type {
  ComputedPhoneNumberBlockStateType,
  PhoneNumberBlockStateType,
  PhoneNumberComponentType,
} from '../../blocks/PhoneNumber/types';
import type {
  PhotoBoothBlockStateType,
  PhotoBoothComponentType,
} from '../../blocks/PhotoBooth/types';
import type { PinInputComponentType, PinInputStateType } from '../../blocks/PinInput/types';
import type { ProgressBarComponentType, ProgressBarStateType } from '../../blocks/Progress/types';
import type { QRCodeBlockStateType, QRCodeComponentType } from '../../blocks/QRCode/types';
import type {
  ComputedRadioButtonBlockStateType,
  RadioButtonBlockStateType,
  RadioButtonComponentType,
} from '../../blocks/RadioButton/types';
import type {
  ComputedRatingBlockStateType,
  RatingBlockStateType,
  RatingComponentType,
} from '../../blocks/Rating/types';
import type { RecordsBlockStateType, RecordsComponentType } from '../../blocks/Records/types';
import type {
  RepeatableBlockStateType,
  RepeatableComponentType,
} from '../../blocks/Repeatable/types';
import type {
  ComputedRichTextBlockStateType,
  RichTextBlockStateType,
  RichTextComponentType,
} from '../../blocks/RichText/types';
import type { ScannerBlockStateType, ScannerComponentType } from '../../blocks/Scanner/types';
import type {
  ComputedSchemaEditorBlockStateType,
  SchemaEditorBlockStateType,
  SchemaEditorComponentType,
} from '../../blocks/SchemaEditor/types';
import type { ScrollableComponentType } from '../../blocks/Scrollable/types';
import type {
  SheetPreviewBlockStateType,
  SheetPreviewComponentType,
} from '../../blocks/SheetPreview/types';
import type { SignatureBlockStateType, SignatureComponentType } from '../../blocks/Signature/types';
import type {
  ComputedSimpleAgentBuilderBlockStateType,
  SimpleAgentBuilderBlockStateType,
  SimpleAgentBuilderComponentType,
} from '../../blocks/SimpleAgentBuilder/types';
import type {
  SingleSelectBlockStateType,
  SingleSelectComponentType,
} from '../../blocks/SingleSelect/types';
import type {
  ComputedSkillEditorBlockStateType,
  SkillEditorBlockStateType,
  SkillEditorComponentType,
} from '../../blocks/SkillEditor/types';
import type {
  ComputedSliderBlockStateType,
  SliderBlockStateType,
  SliderComponentType,
} from '../../blocks/Slider/types';
import type {
  StatCardBlockStateType,
  StatCardComponentType,
  EvaluatedField as StatsCardEvaluatedField,
  EvaluatedTrend as StatsCardEvaluatedTrend,
} from '../../blocks/StatCard/types';
import type {
  StepIndicatorComponentStateType,
  StepIndicatorComponentType,
} from '../../blocks/StepIndicator/types';
import type {
  SteppedContainerComponentType,
  SteppedContainerStateType,
} from '../../blocks/SteppedContainer/types';
import type { StepperComponentStateType, StepperComponentType } from '../../blocks/Stepper/types';
import type {
  StepperV2ComponentStateType,
  StepperV2ComponentType,
} from '../../blocks/StepperV2/types';
import type {
  StepSeparatorComponentStateType,
  StepSeparatorComponentType,
} from '../../blocks/StepSeparator/types';
import type { StoriesBlockStateType, StoriesComponentType } from '../../blocks/Stories/types';
import type {
  SwipeableStackComponentStateType,
  SwipeableStackComponentType,
} from '../../blocks/SwipeableStack/types';
import type {
  ComputedDataTableBlockStateType,
  DataTableBlockStateType,
  DataTableComponentType,
} from '../../blocks/Table/DataTable/types';
import type {
  ComputedGroupTableBlockStateType,
  GroupTableBlockStateType,
  GroupTableComponentType,
} from '../../blocks/Table/GroupTable/types';
import type {
  NestedTableBlockStateType,
  NestedTableComponentType,
} from '../../blocks/Table/NestedTable/types';
import type { UAEntityTableComponentType as TableDeprecatedComponentType } from '../../blocks/Table/UAEntityTable/types';
import type {
  ComputedTabBlockStateType,
  TabsBlockStateType,
  TabsComponentType,
} from '../../blocks/Tabs/types';
import type {
  ComputedTemplateComponentBlockStateType,
  TemplateComponentBlockStateType,
  TemplateComponentComponentType,
} from '../../blocks/TemplateComponent/types';
import type {
  ComputedTextAreaBlockStateType,
  TextAreaBlockStateType,
  TextAreaComponentType,
} from '../../blocks/TextArea/types';
import type { TextInputComponentType, TextInputStateType } from '../../blocks/TextInput/types';
import type { TimelineBlockStateType, TimelineComponentType } from '../../blocks/Timeline/types';
import type { TimerBlockStateType, TimerComponentType } from '../../blocks/Timer/types';
import type {
  ComputedToggleBlockStateType,
  ToggleBlockStateType,
  ToggleComponentType,
} from '../../blocks/Toggle/types';
import type {
  ComputedTracesBlockStateType,
  TracesBlockStateType,
  TracesComponentType,
} from '../../blocks/Traces/types';
import type { TreeViewBlockStateType, TreeViewComponentType } from '../../blocks/TreeView/types';
import type {
  TypographyBlockStateType,
  TypographyComponentType,
} from '../../blocks/Typography/types';
import type { VideoBlockStateType, VideoComponentType } from '../../blocks/Video/types';
import type {
  ButtonBlockStateType,
  ButtonComponentType,
  CameraBlockStateType,
  CameraComponentType,
  CardBlockStateType,
  CardComponentType,
  CheckboxBlockStateType,
  CheckboxComponentType,
  ComputedButtonBlockStateType,
  ComputedCameraBlockStateType,
  ComputedCheckboxBlockStateType,
  ComputedPressableBlockStateType,
  ComputedSwipeableButtonBlockStateType,
  DividerBlockStateType,
  DividerComponentType,
  FieldBuilderBlockStateType,
  FieldBuilderComponentType,
  FiltersBlockStateType,
  FiltersComponentType,
  KanbanBlockStateType,
  KanbanComponentType,
  KeyValueBlockStateType,
  KeyValueComponentType,
  EvaluatedField as KeyValueEvaluatedField,
  LinkBlockStateType,
  LinkComponentType,
  LoaderBlockStateType,
  LoaderComponentType,
  MobileContextualDialogBlockStateType,
  MobileContextualDialogComponentType,
  ModalBlockStateType,
  ModalComponentType,
  PDFViewerBlockStateType,
  PDFViewerComponentType,
  PressableBlockStateType,
  PressableComponentType,
  ScrollableBlockStateType,
  SlotOutletBlockStateType,
  SlotOutletComponentType,
  StackBlockStateType,
  StackComponentType,
  SwipeableButtonBlockStateType,
  SwipeableButtonComponentType,
  TagBlockStateType,
  TagComponentType,
} from '../blocks';
import type { Event } from '../event';
import type { ConditionalPath, DependsOnType, DynamicPath, InterfacePageEntity } from '../page';
import type { ComputedVisibilityPayloadType, VisibilityPayloadType } from '../visibilityPayload';

export type {
  AxisListBlockStateType,
  AxisListComponentType,
  AxisMultiSelectPickerBlockStateType,
  AxisMultiSelectPickerComponentType,
  AxisSingleSelectPickerBlockStateType,
  AxisSingleSelectPickerComponentType,
  ComputedAxisListBlockStateType,
  ComputedAxisMultiSelectPickerBlockStateType,
  ComputedAxisSingleSelectPickerBlockStateType,
  ComputedVisibilityPayloadType,
  VisibilityPayloadType,
};

export type BlockBehavior = {
  detachFromLayout?: boolean;
};

// Any addition to this BlockType is prohibited, add the new field in BlockComponentType
export interface BlockType<T = ComponentsUnionType> {
  /**
   * The unique identifier of the block
   */
  id: string;
  /**
   * The parent block id of the block
   */
  parentId?: BlockId;
  /**
   * The display name of the block to be shown in the editor. It has no use outside the editor.
   */
  // we always set a display name when creating a block, but for backwards compatibility we need to make it optional
  displayName?: string;
  dataSourceIds?: string[];
  /**
   * The query IDs to not suspend
   */
  nonBlockingDataSourceIds?: string[];
  /**
   * Events associated with this component.
   */
  events?: Event[];
  // visibility is used to hide/show the block based on conditions
  visibility?: VisibilityPayloadType;
  fallback?: T;
  component: T;
  permissions?: Record<string, string[]>;
  dP?: DynamicPath[];
  cP?: ConditionalPath[];
  dpOn?: DependsOnType[];
  additional?: {
    isRootBlock?: boolean;
    customCSS?: string;
    isCustomCSSValid?: boolean;
    htmlAttributes?: { key: string; value: string }[];
    /** Marks a Stack as a slot-root created by consumer `+ add`. Used for cascade delete and orphan detection. */
    isSlotRoot?: boolean;
    /** Slot name the block is bound to when isSlotRoot is true. */
    slotName?: string;
  };
  // tells version of the block, used for migrations
  v?: number;
}

export type ComponentsUnionType =
  | AxisListComponentType
  | AxisSingleSelectPickerComponentType
  | AxisMultiSelectPickerComponentType
  | KanbanComponentType
  | StackComponentType
  | ScrollableComponentType
  | TypographyComponentType
  | DividerComponentType
  | LinkComponentType
  | LinkPreviewComponentType
  | ButtonComponentType
  | ImageComponentType
  | ImageGridComponentType
  | LoaderComponentType
  | TableDeprecatedComponentType
  | DataTableComponentType
  | NestedTableComponentType
  | SheetPreviewComponentType
  | TabsComponentType
  | ContainerComponentType
  | CardComponentType
  | SwipeableButtonComponentType
  | SingleSelectComponentType
  | SliderComponentType
  | KeyValueComponentType
  | AlertComponentType
  | RepeatableComponentType
  | TreeViewComponentType
  | ProgressBarComponentType
  | IconButtonComponentType
  | IconComponentType
  | StatCardComponentType
  | MultiStatsCardComponentType
  | CopilotChatComponentType
  | CopilotHistoryComponentType
  | ChartComponentType<string, object>
  | DrawerComponentType
  | BottomSheetComponentType
  | ModalComponentType
  | FormComponentType
  | FieldBuilderComponentType
  | TagComponentType
  | FiltersComponentType
  | ModuleComponentType
  | TemplateComponentComponentType
  | LottieAnimationComponentType
  | CodeEditorComponentType
  | TimelineComponentType
  | FileUploadComponentType
  | BusinessHoursComponentType
  | TextInputComponentType
  | CategoryFilterComponentType
  | CarouselComponentType
  | CameraComponentType
  | ContextualDialogComponentType
  | StepperComponentType
  | SteppedContainerComponentType
  | ButtonGroupComponentType
  | MenuComponentType
  | TimerComponentType
  | CopilotComponentType
  | CommentsComponentType
  | MessageInputComponentType
  | CopilotInputComponentType
  | MediaComponentType
  | DatePickerComponentType
  | AudioPlayerComponentType
  | AudioRecorderComponentType
  | PDFViewerComponentType
  | PasswordComponentType
  | CitationComponentType
  | LocationPreviewComponentType
  | NavigationMenuComponentType
  | NavigationContainerComponentType
  | ChatComponentType
  | VideoComponentType
  | CommentListComponentType
  | CommentInputComponentType
  | ScannerComponentType
  | OrganizationChartComponentType
  | EventsTimelineComponentType
  | QRCodeComponentType
  | CopilotResponseComponentType
  | AvatarComponentType
  | PhoneNumberComponentType
  | PinInputComponentType
  | PressableComponentType
  | AvatarGroupComponentType
  | StoriesComponentType
  | CopilotThreadComponentType
  | CopilotIframeComponentType
  | CopilotTypographyComponentType
  | ToggleComponentType
  | RadioButtonComponentType
  | RatingComponentType
  | AxisIconPickerComponentType
  | AxisCommentsComponentType
  | CheckboxComponentType
  | CascaderComponentType
  | CustomComponentType
  | RichTextComponentType
  | AxisRichTextEditorComponentType
  | AxisSubIssueComposerComponentType
  | AxisNewIssueModalComponentType
  | AxisNewProjectModalComponentType
  | DictationComponentType
  | IframeComponentType
  | KnowledgeBuilderComponentType
  | SimpleAgentBuilderComponentType
  | SkillEditorComponentType
  | TracesComponentType
  | CalendarComponentType
  | SignatureComponentType
  | TextAreaComponentType
  | FloatingActionButtonComponentType
  | MobileContextualDialogComponentType
  | SwipeableStackComponentType
  | FlippableCardComponentType
  | MeetingComponentType
  | HeadlessCopilotComponentType
  | CopilotVoiceComponentType
  | CopilotVoiceTranscriptsComponentType
  | CopilotVideoComponentType
  | PhotoBoothComponentType
  | RecordsComponentType
  | SchemaEditorComponentType
  | GroupTableComponentType
  | BarcodeComponentType
  | StepSeparatorComponentType
  | StepIndicatorComponentType
  | StepperV2ComponentType
  | SlotOutletComponentType;

export const MODULE_AND_TEMPLATE_BLOCK_COMPONENT_TYPES = new Set<string>([
  'Module',
  'TemplateComponent',
]);

export type BlockStateUnionType = (
  | SlotOutletBlockStateType
  | KanbanBlockStateType
  | ButtonBlockStateType
  | KeyValueBlockStateType
  | StackBlockStateType
  | ScrollableBlockStateType
  | ContainerBlockStateType
  | IconBlockStateType
  | DividerBlockStateType
  | ProgressBarStateType
  | IconButtonBlockStateType
  | CardBlockStateType
  | SwipeableButtonBlockStateType
  | SingleSelectBlockStateType
  | SliderBlockStateType
  | ImageBlockStateType
  | ImageGridBlockStateType
  | LinkBlockStateType
  | LinkPreviewBlockStateType
  | MultiStatsCardBlockStateType
  | TabsBlockStateType
  | TypographyBlockStateType
  | StatCardBlockStateType
  | AxisListBlockStateType
  | AxisSingleSelectPickerBlockStateType
  | AxisMultiSelectPickerBlockStateType
  | AlertBlockStateType
  | LottieAnimationBlockStateType
  | LoaderBlockStateType
  | RepeatableBlockStateType
  | TreeViewBlockStateType
  | DataTableBlockStateType
  | NestedTableBlockStateType
  | SheetPreviewBlockStateType
  | ChartStateType
  | DrawerBlockStateType
  | BottomSheetBlockStateType
  | ModalBlockStateType
  | CodeEditorBlockStateType
  | FormBlockStateType
  | FieldBuilderBlockStateType
  | TagBlockStateType
  | FiltersBlockStateType
  | ModuleBlockStateType
  | TemplateComponentBlockStateType
  | TimelineBlockStateType
  | FileUploadBlockStateType
  | FlippableCardBlockStateType
  | BusinessHoursBlockStateType
  | TextInputStateType
  | CategoryFilterStateType
  | CarouselComponentStateType
  | CameraBlockStateType
  | ContextualDialogBlockStateType
  | StepperComponentStateType
  | SteppedContainerStateType
  | ButtonGroupBlockStateType
  | MenuComponentStateType
  | CopilotBlockStateType
  | TimerBlockStateType
  | CommentsBlockStateType
  | MessageInputBlockStateType
  | CopilotInputBlockStateType
  | CopilotHistoryBlockStateType
  | MediaBlockStateType
  | DatePickerBlockStateType
  | AudioPlayerBlockStateType
  | AudioRecorderBlockStateType
  | PDFViewerBlockStateType
  | PasswordStateType
  | CitationBlockStateType
  | LocationPreviewBlockStateType
  | NavigationMenuStateType
  | NavigationContainerStateType
  | ChatBlockStateType
  | VideoBlockStateType
  | CommentListBlockStateType
  | CommentInputBlockStateType
  | ScannerBlockStateType
  | OrganizationChartBlockStateType
  | EventsTimelineBlockStateType
  | QRCodeBlockStateType
  | CopilotResponseBlockStateType
  | AvatarBlockStateType
  | PhoneNumberBlockStateType
  | PinInputStateType
  | PressableBlockStateType
  | AvatarGroupBlockStateType
  | CopilotThreadBlockStateType
  | CopilotIframeBlockStateType
  | CopilotTypographyBlockStateType
  | ToggleBlockStateType
  | RadioButtonBlockStateType
  | RatingBlockStateType
  | AxisIconPickerBlockStateType
  | AxisCommentsBlockStateType
  | CheckboxBlockStateType
  | CascaderBlockStateType
  | CustomBlockStateType
  | RichTextBlockStateType
  | AxisRichTextEditorBlockStateType
  | AxisSubIssueComposerBlockStateType
  | AxisNewIssueModalBlockStateType
  | AxisNewProjectModalBlockStateType
  | DictationBlockStateType
  | IframeBlockStateType
  | KnowledgeBuilderBlockStateType
  | SimpleAgentBuilderBlockStateType
  | SkillEditorBlockStateType
  | TracesBlockStateType
  | CalendarBlockStateType
  | SignatureBlockStateType
  | TextAreaBlockStateType
  | StoriesBlockStateType
  | FloatingActionButtonBlockStateType
  | MobileContextualDialogBlockStateType
  | SwipeableStackComponentStateType
  | MeetingBlockStateType
  | HeadlessCopilotBlockStateType
  | CopilotVoiceBlockStateType
  | CopilotVoiceTranscriptsBlockStateType
  | CopilotVideoBlockStateType
  | PhotoBoothBlockStateType
  | RecordsBlockStateType
  | SchemaEditorBlockStateType
  | GroupTableBlockStateType
  | BarcodeBlockStateType
  | StepSeparatorComponentStateType
  | StepIndicatorComponentStateType
  | StepperV2ComponentStateType
) & {
  // can be undefined if the block is an instance of repeatable block
  id?: string;
  //for run time block state
  originalBlockId?: string;
  context?: Record<string, unknown>;
  events?: Event[];
  visibility?: VisibilityPayloadType;
  additional?: BlockType['additional'];
};

export type ComputedBlockStateUnionType = (
  | KanbanBlockStateType
  | ComputedButtonBlockStateType
  | KeyValueBlockStateType<KeyValueEvaluatedField>
  | StackBlockStateType
  | ContainerBlockStateType
  | IconBlockStateType
  | DividerBlockStateType
  | SwipeableButtonBlockStateType
  | SingleSelectBlockStateType
  | ComputedSliderBlockStateType
  | ProgressBarStateType
  | IconButtonBlockStateType
  | CardBlockStateType
  | ImageBlockStateType
  | ImageGridBlockStateType
  | LinkBlockStateType
  | LinkPreviewBlockStateType
  | MultiStatsCardBlockStateType<StatsCardEvaluatedField, StatsCardEvaluatedTrend>
  | ComputedTabBlockStateType
  | TypographyBlockStateType
  | StatCardBlockStateType<StatsCardEvaluatedField, StatsCardEvaluatedTrend>
  | AlertBlockStateType
  | LoaderBlockStateType
  | RepeatableBlockStateType<unknown>
  | TreeViewBlockStateType<unknown>
  | ComputedDataTableBlockStateType
  | ComputedAxisListBlockStateType
  | ComputedAxisSingleSelectPickerBlockStateType
  | ComputedAxisMultiSelectPickerBlockStateType
  | SheetPreviewBlockStateType
  | ComputedChartStateType<object>
  | DrawerBlockStateType
  | BottomSheetBlockStateType
  | ModalBlockStateType
  | CodeEditorBlockStateType
  | FormBlockStateType
  | FieldBuilderBlockStateType
  | TagBlockStateType
  | LottieAnimationBlockStateType
  | FiltersBlockStateType
  | ComputedModuleBlockStateType
  | ComputedTemplateComponentBlockStateType
  | TimelineBlockStateType<Record<string, string | number>[]>
  | FileUploadBlockStateType
  | FlippableCardBlockStateType
  | BusinessHoursBlockStateType<BusinessHourValueType>
  | TextInputStateType
  | CategoryFilterStateType
  | CarouselComponentStateType
  | ComputedCameraBlockStateType
  | ContextualDialogBlockStateType
  | StepperComponentStateType
  | SteppedContainerStateType
  | ButtonGroupBlockStateType
  | MenuComponentStateType
  | CopilotBlockStateType
  | TimerBlockStateType
  | CommentsBlockStateType
  | ComputedSwipeableButtonBlockStateType
  | MessageInputBlockStateType
  | CopilotInputBlockStateType
  | MediaBlockStateType
  | DatePickerBlockStateType
  | AudioPlayerBlockStateType
  | AudioRecorderBlockStateType
  | PDFViewerBlockStateType
  | PasswordStateType
  | CitationBlockStateType
  | LocationPreviewBlockStateType
  | NavigationMenuStateType
  | NavigationContainerStateType
  | ChatBlockStateType
  | VideoBlockStateType
  | CommentListComputedBlockStateType
  | CommentInputBlockStateType
  | ScannerBlockStateType
  | MessageInputComputedBlockStateType
  | CopilotInputComputedBlockStateType
  | OrganizationChartComputedBlockStateType
  | EventsTimelineBlockStateType
  | QRCodeBlockStateType
  | CopilotResponseBlockStateType
  | CopilotHistoryBlockStateType
  | AvatarBlockStateType
  | ComputedPhoneNumberBlockStateType
  | PinInputStateType
  | ComputedPressableBlockStateType
  | AvatarGroupBlockStateType
  | CopilotThreadBlockStateType
  | ComputedToggleBlockStateType
  | ComputedRadioButtonBlockStateType
  | ComputedRatingBlockStateType
  | ComputedAxisIconPickerBlockStateType
  | ComputedAxisCommentsBlockStateType
  | ComputedCheckboxBlockStateType
  | CascaderBlockStateType
  | ComputedCustomBlockStateType
  | ComputedRichTextBlockStateType
  | ComputedAxisRichTextEditorBlockStateType
  | ComputedAxisSubIssueComposerBlockStateType
  | ComputedAxisNewIssueModalBlockStateType
  | ComputedAxisNewProjectModalBlockStateType
  | IframeBlockStateType
  | ComputedKnowledgeBuilderBlockStateType
  | ComputedSimpleAgentBuilderBlockStateType
  | ComputedSkillEditorBlockStateType
  | ComputedTracesBlockStateType
  | DictationBlockStateType
  | CalendarBlockStateType
  | SignatureBlockStateType
  | ComputedTextAreaBlockStateType
  | StoriesBlockStateType
  | MobileContextualDialogBlockStateType
  | SwipeableStackComponentStateType
  | MeetingBlockStateType
  | HeadlessCopilotBlockStateType
  | CopilotVoiceBlockStateType
  | CopilotVoiceTranscriptsBlockStateType
  | CopilotVideoBlockStateType
  | PhotoBoothBlockStateType
  | RecordsBlockStateType
  | ComputedSchemaEditorBlockStateType
  | ComputedGroupTableBlockStateType
  | BarcodeBlockStateType
  | CopilotIframeBlockStateType
  | CopilotTypographyBlockStateType
  | StepSeparatorComponentStateType
  | StepIndicatorComponentStateType
  | StepperV2ComponentStateType
) & {
  id: string;
  //for run time block state
  originalBlockId?: string;
  context?: Record<string, unknown>;
  events?: Event[];
  visibility?: ComputedVisibilityPayloadType;
  additional?: BlockType['additional'];
};

export enum ComposedBlockComponentType {
  RepeatableList = 'RepeatableList',
  RepeatableGrid = 'RepeatableGrid',
  Navigation = 'Navigation',
}

export type ComponentTypeUnionType = ComponentsUnionType['componentType'];

export type BlockTypeUnionType =
  | BlockType['component']['componentType']
  | ComposedBlockComponentType;

export type BlockComponentType<
  ComponentType extends string,
  Content extends object,
  Appearance extends object,
  Slots = undefined,
> = {
  componentType: ComponentType;
  content: Content;
  appearance?: Appearance & { customStyles?: object };
  context?: Record<string, unknown>;
  slots?: Slots;
};

export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

export type BlockComponentWithRequiredAppearance<
  ComponentType extends string,
  Content extends object,
  Appearance extends object,
  Slots = undefined,
> = WithRequired<BlockComponentType<ComponentType, Content, Appearance, Slots>, 'appearance'>;

export type BlockId = string | null;
export type BlockIds = BlockId[];

export type BlockComponentStateId = {
  id: string;
  originalBlockId?: string;
};

export type BlockCompositionData = {
  // Controls whether the display name of the block should be preserved during the creation process.
  // If set to true, the display name provided for the block will be maintained and not automatically altered
  // or overridden by any naming conventions or automatic generation logic. This is useful when the display name
  // needs to remain consistent for tracking, identification, or user interface purposes, even after the block
  // is created. If false or undefined, the display name may be generated or modified according to the system's
  // standard rules for naming blocks during creation.
  persistDisplayName?: boolean;
  /**
   * whatever id we pass i the childBlocks and rootBlock will be persisted
   * if not provided, a new id will be generated
   */
  persistId?: boolean;
  /**
   * whatever id we pass in the rootBlock will be persisted, childBlocks ids will be generated
   * if not provided, a new id will be generated
   */
  persistRootBlockId?: boolean;
  childBlocks?: Record<string, BlockType>;
  companionBlocks?: Record<string, BlockType>;
  mergeWithBlockInitialFormData?: boolean;
  devices?: InterfacePageEntity['properties']['devices'];
  compose?: ({
    rootBlock,
    childBlocks,
    companionBlocks,
    interfaceRootBlock,
  }: {
    rootBlock: BlockType;
    childBlocks: Record<string, BlockType>;
    companionBlocks?: Record<string, BlockType>;
    interfaceRootBlock?: BlockType<StackComponentType>;
  }) => {
    rootBlock: BlockType;
    childBlocks: Record<string, BlockType>;
    companionBlocks?: Record<string, BlockType>;
    interfaceRootBlock?: BlockType<StackComponentType>;
  };
};

export type CreateCompositeBlockActionProps = {
  block: Omit<BlockType, 'id' | 'displayName'> & { id?: string; displayName?: string };
  blockCompositionData: BlockCompositionData;
  /** theme-renamed label; seeds the composite root block's auto-generated name */
  themedComponentDisplayName?: string;
};

export type CreateCompositeBlockActionReturnType =
  | { success: false; blockId: null }
  | { success: true; blockId: BlockId };
